import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchPrintletCities,
  fetchPrintletCityMappings,
  fetchPrintletSettings,
  savePrintletCityMapping,
} from "../../api/PrintletAPIs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";
import {
  Settings,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Link2,
  MapPin,
  Save,
  Shield,
  Server,
  Key,
  Webhook,
  Wallet,
  ArrowLeft,
} from "lucide-react";

function StatusPill({ ok, labelOk = "مضبوط", labelNo = "غير مضبوط" }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
      <CheckCircle2 size={11} /> {labelOk}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium border border-slate-200">
      <XCircle size={11} /> {labelNo}
    </span>
  );
}

function SettingRow({ icon: Icon, label, value, accent = "slate" }) {
  const accents = {
    indigo: "text-indigo-500",
    emerald: "text-emerald-500",
    amber: "text-amber-500",
    rose: "text-rose-500",
    slate: "text-slate-400",
  };
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Icon size={15} className={accents[accent]} />
        {label}
      </div>
      <div className="text-sm font-medium text-slate-800 text-left">{value}</div>
    </div>
  );
}

export default function PrintletSettingsPage() {
  const { authState } = useAuth();
  const canMutate = canMutateOrders(authState.role);
  const { data: settings } = useQuery({ queryKey: ["printletSettings"], queryFn: fetchPrintletSettings });
  const { data: cities = [] } = useQuery({ queryKey: ["printletCities"], queryFn: fetchPrintletCities });
  const { data: mappings = [], refetch } = useQuery({
    queryKey: ["printletCityMappings"],
    queryFn: fetchPrintletCityMappings,
  });
  const [state, setState] = useState("");
  const [cityId, setCityId] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!state.trim() || !cityId) {
      toast.error("اختر المحافظة والمدينة");
      return;
    }
    setSaving(true);
    try {
      const city = cities.find((c) => (c.externalId || c.ExternalId) === cityId);
      await savePrintletCityMapping({
        girabatState: state.trim(),
        printletCityId: cityId,
        printletCityName: city?.name || city?.Name,
        isActive: true,
      });
      toast.success("تم حفظ ربط المدينة");
      setState("");
      setCityId("");
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <ToastContainer position="top-center" />

      {/* Live warning */}
      {settings?.environment === "live" && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="p-2 rounded-xl bg-amber-100 text-amber-700 shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div className="text-sm text-amber-900">
            <b>بيئة الإنتاج (Live):</b> الإرسال والدفع هيخصموا من محفظة Printlet الحقيقية ويبدأوا تنفيذ فعلي.
          </div>
        </div>
      )}

      {/* Current settings */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
            <Settings size={18} />
          </div>
          <h3 className="font-bold text-slate-800">الإعدادات الحالية</h3>
          <span className="mr-auto">
            {settings?.enabled ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
                <CheckCircle2 size={12} /> مفعّل
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-medium border border-slate-200">
                <XCircle size={12} /> معطّل
              </span>
            )}
          </span>
        </div>

        <div className="space-y-0">
          <SettingRow
            icon={Server}
            label="البيئة"
            value={
              <span
                className={`px-2 py-0.5 rounded-lg text-xs font-bold ${
                  settings?.environment === "live"
                    ? "bg-rose-50 text-rose-700"
                    : "bg-sky-50 text-sky-700"
                }`}
              >
                {settings?.environment || "—"}
              </span>
            }
          />
          <SettingRow icon={Link2} label="Base URL" value={<span className="font-mono text-xs">{settings?.apiBaseUrl || "—"}</span>} />
          <SettingRow
            icon={Key}
            label="المفتاح"
            accent="amber"
            value={<span className="font-mono text-xs">{settings?.apiKeyMasked || "—"}</span>}
          />
          <SettingRow
            icon={Webhook}
            label="Webhook secret"
            accent="indigo"
            value={<StatusPill ok={settings?.webhookSecretConfigured} />}
          />
          <SettingRow
            icon={Shield}
            label="تأكيد COD تلقائي"
            accent="emerald"
            value={<StatusPill ok={settings?.autoVerifyCod} labelOk="نعم" labelNo="لا" />}
          />
          <SettingRow
            icon={Wallet}
            label="الدفع من المحفظة تلقائياً"
            accent="indigo"
            value={<StatusPill ok={settings?.autoPayFromWallet} labelOk="نعم" labelNo="لا" />}
          />
        </div>

        <div className="mt-4 p-3 bg-slate-50 rounded-xl text-xs text-slate-500 leading-relaxed">
          غيّر القيم من Printlet في <code className="bg-white px-1 rounded">appsettings</code> أو متغيرات{" "}
          <code className="bg-white px-1 rounded">PRINTLET_API_KEY</code> و{" "}
          <code className="bg-white px-1 rounded">PRINTLET_WEBHOOK_SECRET</code>. المفتاح لا يُعرض أبداً في الواجهة.
        </div>
      </div>

      {/* City mapping */}
      {canMutate && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
              <MapPin size={18} />
            </div>
            <h3 className="font-bold text-slate-800">ربط محافظة جراباتكم بمدينة Printlet</h3>
          </div>

          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 mb-4">
            <input
              className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition"
              placeholder="اسم المحافظة في جراباتكم"
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
            <select
              className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition"
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
            >
              <option value="">مدينة Printlet</option>
              {cities.map((c) => (
                <option key={c.externalId || c.id} value={c.externalId}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition"
            >
              <Save size={16} />
              حفظ
            </button>
          </div>

          {/* Existing mappings */}
          {mappings.length > 0 ? (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="p-2.5 text-right font-semibold">محافظة جراباتكم</th>
                    <th className="p-2.5 text-right font-semibold">مدينة Printlet</th>
                    <th className="p-2.5 text-right font-semibold">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="p-2.5 font-medium text-slate-700">{m.girabatState}</td>
                      <td className="p-2.5 text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <ArrowLeft size={12} className="text-slate-400" />
                          {m.printletCityName || m.printletCityId}
                        </span>
                      </td>
                      <td className="p-2.5">
                        <StatusPill ok={m.isActive !== false} labelOk="نشط" labelNo="معطّل" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <MapPin size={28} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">لا توجد روابط مدن بعد</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}